import type { AppSaveBarEventCallbacks } from "./app-save-bar-event";
import type { AppSaveBarScope } from "./app-save-bar.enum";
import type { AppSaveBarProps, AppSaveBarStateKey } from "./app-save-bar.type";

import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";

import {
  useAppSaveBarEventEmitter,
  useAppSaveBarEventListener,
} from "./app-save-bar-event";
import { shouldShowAppSaveBar } from "./app-save-bar.helper";
import {
  appSaveBarItemAtom,
  resetSaveBarItemAtom,
  setSaveBarItemAtom,
} from "./app-save-bar.state";

export const useAppSaveBarStatus = (scope: AppSaveBarScope) => {
  return useAtomValue(appSaveBarItemAtom(scope));
};

export const useAppSaveBar = (scope: AppSaveBarScope) => {
  const setSaveBar = useSetAtom(setSaveBarItemAtom);

  const setSaveBarItem = useCallback(
    (key: AppSaveBarStateKey, value: boolean) => {
      setSaveBar({ scope, key, value });
    },
    [scope, setSaveBar]
  );

  return {
    setSaveBar: setSaveBarItem,
  };
};

export const useAppSaveBarInit = ({
  scope,
  tabs,
  tabKey,
  date,
  loading: loadingProps,
}: AppSaveBarProps) => {
  const [params] = useSearchParams();

  const {
    ready,
    loading: loadingState,
    dirty,
    saving,
  } = useAppSaveBarStatus(scope);

  const { emit } = useAppSaveBarEventEmitter();
  const showSaveBar = shouldShowAppSaveBar({ ready, tabs, tabKey, params });

  // Block navigation when form saving or form has changes not saved yet.
  // But do not show modal when form saving...
  const shouldBlock = (dirty || saving) ?? false;
  const shouldShowModal = !saving;

  return {
    showSaveBar,
    date,
    loading: loadingProps || loadingState || saving,
    hasChanges: dirty,
    onSave: () => emit("save-click", scope),
    onCancel: () => emit("cancel-click", scope),
    shouldBlock,
    shouldShowModal,
  };
};

export const useAppSaveBarHandler = (
  scope: AppSaveBarScope,
  actions: AppSaveBarEventCallbacks
) => {
  const { setSaveBar } = useAppSaveBar(scope);
  const resetSaveBar = useSetAtom(resetSaveBarItemAtom);

  useEffect(() => {
    setSaveBar("ready", true); // Init
    return () => resetSaveBar(scope); // Cleanup
  }, [scope, setSaveBar, resetSaveBar]);

  // Handle events listener
  useAppSaveBarEventListener(scope, actions);
};

/**
 * Status sync using this hook cannot be defined manually (they will be overriden by this sync)
 */
export const useAppSaveBarSync = (
  scope: AppSaveBarScope,
  key: AppSaveBarStateKey,
  value?: boolean
) => {
  const { setSaveBar } = useAppSaveBar(scope);

  useEffect(() => {
    if (typeof value !== "boolean") return;
    setSaveBar(key, value);
  }, [key, value, setSaveBar]);
};
